/**
 * Surrogate-safe string helpers.
 *
 * JavaScript strings are sequences of UTF-16 code units, so `slice`/`substring`
 * will happily cut a non-BMP character (emoji, rarer CJK, maths symbols, most
 * pictographs) in half and leave an unpaired surrogate behind. Whether anyone
 * notices depends entirely on where that string lands next:
 *
 *  - a `text` column silently repairs it — Node encodes the lone surrogate as
 *    U+FFFD on the way to UTF-8, so the row just stores a replacement char;
 *  - a `json`/`jsonb` column HARD FAILS — `JSON.stringify` escapes the lone
 *    surrogate as the ASCII sequence `\ud83d`, which survives all the way to
 *    Postgres's JSON parser and is rejected with 22P02 "invalid input syntax
 *    for type json" ("Unicode low surrogate must follow a high surrogate").
 *
 * That second case cost us a delivery in prod on 2026-08-19: an evidence string
 * clipped mid-emoji by the tool-result budget made the whole `messages` INSERT
 * fail, so a scheduled report the agent had spent ~5 minutes producing was never
 * persisted and never sent — after 58 LLM calls, the user got nothing.
 *
 * The rule: clip free text with `truncateChars`, never a bare `slice`, and
 * sanitize anything bound for a jsonb column with `stripLoneSurrogates`. The
 * two are belt and braces — the first removes the cause, the second is the
 * backstop for text that arrives already broken (or from a clip we missed).
 */

/**
 * True when the last code unit is a high surrogate with no low surrogate to
 * pair with — i.e. a cut landed in the middle of a surrogate pair.
 */
const endsMidSurrogatePair = (text: string): boolean => {
  const last = text.charCodeAt(text.length - 1);
  return last >= 0xd800 && last <= 0xdbff;
};

/**
 * `text.slice(0, max)` that never splits a surrogate pair — the straddling
 * character is dropped whole rather than half-kept, so the result is always
 * at most `max` code units and always well-formed UTF-16.
 *
 * Use this anywhere free text (tool output, user input, LLM output, error
 * messages) is clipped to a budget.
 */
export const truncateChars = (text: string, max: number): string => {
  if (max <= 0) return "";
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  return endsMidSurrogatePair(cut) ? cut.slice(0, -1) : cut;
};

/**
 * `text.slice(start)` that never begins on the low half of a surrogate pair —
 * the mirror of `truncateChars` for tail slices.
 */
export const sliceCharsFrom = (text: string, start: number): string => {
  if (start <= 0) return text;
  const cut = text.slice(start);
  const first = cut.charCodeAt(0);
  return first >= 0xdc00 && first <= 0xdfff ? cut.slice(1) : cut;
};

/**
 * Nudge a split offset off the seam of a surrogate pair, so `text.slice(0, i)`
 * and `text.slice(i)` are each well-formed.
 *
 * Splitters (chunking for a channel's message ceiling, wrapping a long line)
 * keep BOTH halves, so `truncateChars` is the wrong tool there — it would drop
 * the straddling character from the head without putting it in the tail, which
 * turns a lossless split into silent data loss.
 */
export const safeSplitIndex = (text: string, index: number): number => {
  if (index <= 0 || index >= text.length) return index;
  const prev = text.charCodeAt(index - 1);
  const next = text.charCodeAt(index);
  const insidePair = prev >= 0xd800 && prev <= 0xdbff && next >= 0xdc00 && next <= 0xdfff;
  return insidePair ? index - 1 : index;
};

/**
 * Matches any unpaired surrogate: a high one not followed by a low one, or a
 * low one not preceded by a high one.
 */
const LONE_SURROGATE_RE = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?<![\uD800-\uDBFF])[\uDC00-\uDFFF]/g;

/**
 * Replace unpaired surrogates with U+FFFD (the Unicode replacement character —
 * exactly what a UTF-8 encoder would have produced anyway).
 *
 * Replacing rather than deleting keeps the damage visible in the stored data:
 * a "" in a row is a signal that something upstream clipped badly, where a
 * silent deletion would just look like slightly short text.
 */
export const stripLoneSurrogates = (text: string): string =>
  text.replace(LONE_SURROGATE_RE, "�");

/**
 * `text.slice(-count)` that never begins on the low half of a surrogate pair.
 * The head/tail truncators ("keep the start and the end, elide the middle")
 * need this for their tail as much as they need `truncateChars` for their head.
 */
/** Clip to `maxChars` keeping BOTH ends (65% head / 35% tail) with an explicit
 *  omission marker. Use over `truncateChars` whenever the END of the text
 *  carries meaning a reader must see — e.g. a run transcript whose final
 *  lines prove what was delivered (head-only truncation of a compiled run's
 *  trace made a quality judge score a delivered report as "never delivered",
 *  2026-09-04). Surrogate-safe via the shared slice helpers. */
export const truncateMiddle = (text: string, maxChars: number): string => {
  if (text.length <= maxChars) return text;
  const head = Math.floor(maxChars * 0.65);
  const tail = maxChars - head;
  return `${truncateChars(text, head)}\n\n[...${text.length - maxChars} chars omitted...]\n\n${sliceCharsTail(text, tail)}`;
};

export const sliceCharsTail = (text: string, count: number): string => {
  if (count <= 0) return "";
  if (count >= text.length) return text;
  return sliceCharsFrom(text, text.length - count);
};

/** True if the string contains at least one unpaired surrogate. Test-facing. */
export const hasLoneSurrogate = (text: string): boolean =>
  // A fresh regex per call: `LONE_SURROGATE_RE` is global, and `.test()` on a
  // global regex advances `lastIndex`, which makes repeated calls alternate
  // between true and false.
  new RegExp(LONE_SURROGATE_RE.source).test(text);
